import React, { useState, useRef, useMemo } from 'react'
import type { DayViewProps, CalendarEvent } from '../types'
import { AppointmentCard } from '../components/AppointmentCard'
import { TimeLabels } from '../components/TimeLabels'
import { TimeSlotGrid } from '../components/TimeSlotGrid'
import { CurrentTimeIndicator } from '../components/CurrentTimeIndicator'
import { getEventsForDay, isToday } from '../utils/time'
import {
  calculateOverlapLayout,
  getOverlapEventStyle,
  DEFAULT_SLOT_HEIGHT,
} from '../utils/positioning'
import { formatDateWithDay, getUILabel } from '../utils/locale'

export const DayView: React.FC<DayViewProps> = ({
  date,
  events,
  slotDuration,
  startHour,
  endHour,
  editable,
  locale,
  selectedEventIds = [],
  onEventClick,
  onSlotClick,
  onEventDrop,
}) => {
  const [draggedEvent, setDraggedEvent] = useState<CalendarEvent | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const dayEvents = getEventsForDay(events, date)
  const slotHeight = DEFAULT_SLOT_HEIGHT
  const totalSlots = ((endHour - startHour) * 60) / slotDuration
  const gridHeight = totalSlots * slotHeight

  // Calculate overlap layout for events
  const eventLayouts = useMemo(
    () => calculateOverlapLayout(dayEvents, startHour, slotDuration, slotHeight),
    [dayEvents, startHour, slotDuration, slotHeight]
  )

  const handleDragStart = (event: CalendarEvent) => (e: React.DragEvent) => {
    setDraggedEvent(event)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDrop = (e: React.DragEvent, hour: number, minute: number) => {
    e.preventDefault()
    if (draggedEvent && editable) {
      const newStart = new Date(date)
      newStart.setHours(hour, minute, 0, 0)
      onEventDrop?.(draggedEvent, newStart)
      setDraggedEvent(null)
    }
  }

  const handleSlotClick = (clickDate: Date, hour: number, minute: number) => {
    if (editable) {
      onSlotClick?.(clickDate, hour, minute)
    }
  }

  return (
    <div className="calendar-day-view">
      {/* Day Header */}
      <div
        style={{
          padding: '12px 16px',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: isToday(date) ? '#fef3c7' : '#f9fafb',
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: '16px',
            fontWeight: 600,
            color: '#111827',
            textTransform: 'capitalize',
          }}
        >
          {formatDateWithDay(date, locale)}
        </h3>
        {dayEvents.length > 0 && (
          <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#6b7280' }}>
            {dayEvents.length} {dayEvents.length === 1 ? 'termin' : 'termina'}
          </p>
        )}
      </div>

      {/* Time Grid Container */}
      <div
        ref={containerRef}
        style={{
          display: 'flex',
          maxHeight: '600px',
          overflowY: 'auto',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          margin: '16px',
        }}
      >
        {/* Time Labels */}
        <TimeLabels
          startHour={startHour}
          endHour={endHour}
          slotDuration={slotDuration}
          slotHeight={slotHeight}
        />

        {/* Slots and Events */}
        <div
          style={{
            flex: 1,
            position: 'relative',
            minHeight: gridHeight,
          }}
        >
          {/* Time Slot Grid */}
          <TimeSlotGrid
            date={date}
            startHour={startHour}
            endHour={endHour}
            slotDuration={slotDuration}
            slotHeight={slotHeight}
            onSlotClick={handleSlotClick}
            onDrop={handleDrop}
          />

          {/* Events Overlay */}
          {eventLayouts.map((layout) => {
            const style = getOverlapEventStyle(layout)
            const leftMargin = 70 // Time labels width
            const rightMargin = 12
            const isSelected = selectedEventIds.includes(layout.event.id)

            return (
              <div
                key={layout.event.id}
                style={{
                  position: 'absolute',
                  top: style.top + 1,
                  left: leftMargin,
                  right: rightMargin,
                  height: style.height - 2,
                  zIndex: isSelected ? 2 : 1,
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    left: style.left,
                    width: style.width,
                    height: '100%',
                    paddingRight: layout.totalColumns > 1 ? '2px' : '0',
                  }}
                  onClick={(e) => {
                    const modifiers = {
                      ctrl: e.ctrlKey || e.metaKey,
                      shift: e.shiftKey,
                    }
                    onEventClick?.(layout.event, modifiers)
                  }}
                >
                  <AppointmentCard
                    event={layout.event}
                    locale={locale}
                    draggable={editable}
                    selected={isSelected}
                    onDragStart={handleDragStart(layout.event)}
                  />
                </div>
              </div>
            )
          })}

          {/* Current Time Indicator */}
          {isToday(date) && (
            <CurrentTimeIndicator
              startHour={startHour}
              slotDuration={slotDuration}
              slotHeight={slotHeight}
            />
          )}
        </div>
      </div>

      {/* Empty State */}
      {dayEvents.length === 0 && (
        <div
          style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: '#6b7280',
          }}
        >
          <p style={{ margin: 0, fontSize: '14px' }}>
            {getUILabel('noEvents', locale)}
          </p>
          {editable && (
            <p style={{ margin: '8px 0 0', fontSize: '12px' }}>
              {locale === 'sr'
                ? 'Kliknite na slot da zakazete termin'
                : 'Click on a slot to schedule an appointment'}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
